/**
 * Stub implementation of ComputerStore
 * All WebRTC screen sharing functionality has been removed from this codebase.
 * This store remains only for compatibility with existing code.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getPhaserGameSync, isPhaserGameReady } from '../PhaserGame'
import { sanitizeId } from '../util'

// Stub type for removed MediaConnection
interface StubMediaConnection {
    peer: string
    metadata?: any
}

// Interface for serializable calls (without actual WebRTC references)
interface SerializableCall {
    peer: string
    metadata?: any
    type: string
}

// Stub interface for MediaStream to avoid WebRTC dependency
interface StubMediaStream {
    _type: string
    _hasVideo: boolean
    _hasAudio: boolean
    getTracks?: () => Array<{ stop: () => void }>
}

// Create a MediaStreamWrapper that doesn't depend on actual MediaStream
interface MediaStreamWrapper {
    _type: string
    _hasVideo: boolean
    _hasAudio: boolean
    _actualStream?: StubMediaStream
}

interface ComputerState {
    computerDialogOpen: boolean
    computerId: null | string
    myStream: null | StubMediaStream | MediaStreamWrapper
    peerStreams: Map<
        string,
        {
            stream: StubMediaStream
            call: SerializableCall
        }
    >
    shareScreenManager?: {
        myStream: any | null
        startScreenShare: () => void
        stopScreenShare: () => void
        onUserJoined: (userId: string) => void
        onUserLeft: (userId: string) => void
    } | null
}

const initialState: ComputerState = {
    computerDialogOpen: false,
    computerId: null,
    myStream: null,
    peerStreams: new Map(),
    shareScreenManager: null,
}

export const computerSlice = createSlice({
    name: 'computer',
    initialState,
    reducers: {
        openComputerDialog: (
            state,
            action: PayloadAction<{ computerId: string; myUserId: string }>
        ) => {
            console.warn('openComputerDialog called, but screen sharing functionality has been removed')

            // Safely access Phaser game (may not be initialized yet)
            const phaserGame = getPhaserGameSync()
            if (phaserGame && isPhaserGameReady()) {
                const game = phaserGame.scene?.keys?.game as any
                game?.disableKeys?.()
            }

            state.computerDialogOpen = true
            state.computerId = action.payload.computerId
        },
        closeComputerDialog: (state) => {
            console.warn('closeComputerDialog called, but screen sharing functionality has been removed')

            // Cache computerId before we null it out
            const computerId = state.computerId

            // Clean up safely without assuming actual MediaStream functionality
            if (state.myStream) {
                if ((state.myStream as MediaStreamWrapper)._actualStream &&
                    (state.myStream as MediaStreamWrapper)._actualStream?.getTracks) {
                    // Safely handle the case if getTracks exists
                    const tracks = (state.myStream as MediaStreamWrapper)._actualStream?.getTracks?.() || []
                    tracks.forEach(track => track.stop && track.stop())
                } else if ((state.myStream as StubMediaStream).getTracks) {
                    // Handle the direct MediaStream case
                    const tracks = (state.myStream as StubMediaStream).getTracks?.() || []
                    tracks.forEach(track => track.stop && track.stop())
                }
            }

            // Tell server the computer dialog is closed
            // Safely access Phaser game (may not be initialized yet)
            const phaserGame = getPhaserGameSync()
            if (phaserGame && isPhaserGameReady()) {
                const game = phaserGame.scene?.keys?.game as any
                game?.enableKeys?.()
                
                // Use the cached computerId instead of accessing it from state after changes
                if (computerId) {
                    game?.network?.disconnectFromComputer?.(computerId)
                }
            }

            state.computerDialogOpen = false
            state.myStream = null
            state.computerId = null
            state.peerStreams.clear()
        },
        setMyStream: (state, action: PayloadAction<null | StubMediaStream | MediaStreamWrapper>) => {
            console.warn('setMyStream called, but screen sharing functionality has been removed')
            state.myStream = action.payload
        },
        addVideoStream: (
            state,
            action: PayloadAction<{
                id: string
                call: SerializableCall | StubMediaConnection
                stream: StubMediaStream | MediaStreamWrapper
            }>
        ) => {
            console.warn('addVideoStream called, but screen sharing functionality has been removed')

            // Handle both SerializableCall and StubMediaConnection
            let callToStore: SerializableCall

            if ('type' in action.payload.call) {
                // It's already a SerializableCall
                callToStore = action.payload.call as SerializableCall
            } else {
                // It's a StubMediaConnection, convert to SerializableCall
                const mediaCall = action.payload.call as StubMediaConnection
                callToStore = {
                    peer: mediaCall.peer,
                    metadata: mediaCall.metadata,
                    type: 'stub-call',
                }
            }

            // Handle both direct StubMediaStream and MediaStreamWrapper
            let streamToStore: StubMediaStream
            if ((action.payload.stream as MediaStreamWrapper)._type === 'MediaStreamWrapper') {
                // It's a MediaStreamWrapper, use the _actualStream property if available
                streamToStore = (action.payload.stream as MediaStreamWrapper)._actualStream || {
                    _type: 'StubMediaStream',
                    _hasVideo: false,
                    _hasAudio: false
                }
            } else {
                // It's a direct StubMediaStream
                streamToStore = action.payload.stream as StubMediaStream
            }

            state.peerStreams.set(sanitizeId(action.payload.id), {
                call: callToStore,
                stream: streamToStore,
            })
        },
        removeVideoStream: (state, action: PayloadAction<string>) => {
            console.warn('removeVideoStream called, but screen sharing functionality has been removed')
            state.peerStreams.delete(sanitizeId(action.payload))
        },
    },
})

export const {
    closeComputerDialog,
    openComputerDialog,
    setMyStream,
    addVideoStream,
    removeVideoStream,
} = computerSlice.actions

export default computerSlice.reducer 